import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../../../../app/store";
import {botApi, RequestStatus} from "../../../../../api/apiConfiguration";
import {
    EventType,
    TranscriptRoutingSettingsResponse,
    UpdateTranscriptRoutingRequest
} from "../../../../../api/generated";

export interface RoutingDepartment {
    id?: number;
    name: string;
    summaryGuidance: string;
    filtrationEnabled: boolean;
    eventTypes: EventType[];
}

export interface RoutingLocationDepartment {
    departmentId?: number;
    departmentName?: string;
    emailAddresses: string[];
}

export interface RoutingLocation {
    locationPublicId: string;
    locationName: string;
    catchAllRecipients: string[];
    departments: RoutingLocationDepartment[];
}

export interface TranscriptRoutingSettingsUI {
    enabled: boolean;
    departments: RoutingDepartment[];
    locations: RoutingLocation[];
}

interface TranscriptRoutingState {
    saved?: TranscriptRoutingSettingsUI;
    updateRequest: TranscriptRoutingSettingsUI;
    requestStatus: RequestStatus;
    updateStatus: RequestStatus;
    hasUnsavedChanges: boolean;
}

const emptySettings: TranscriptRoutingSettingsUI = {enabled: false, departments: [], locations: []};

const matchesDepartment = (
    mapping: RoutingLocationDepartment,
    department: Pick<RoutingDepartment, "id" | "name">
): boolean => department.id !== undefined
    ? mapping.departmentId === department.id
    : mapping.departmentId === undefined && mapping.departmentName === department.name;

const mapResponse = (response: TranscriptRoutingSettingsResponse): TranscriptRoutingSettingsUI => ({
    enabled: Boolean(response.enabled),
    departments: (response.departments || []).map(department => ({
        id: department.id,
        name: department.name || "",
        summaryGuidance: department.summaryGuidance || "",
        filtrationEnabled: Boolean(department.filtrationEnabled),
        eventTypes: Array.from(department.eventTypes || []) as EventType[]
    })),
    locations: (response.locations || []).map(location => ({
        locationPublicId: location.locationPublicId || "",
        locationName: location.locationName || "",
        catchAllRecipients: location.catchAllRecipients || [],
        departments: (location.departments || []).map(department => ({
            departmentId: department.departmentId,
            departmentName: department.departmentName,
            emailAddresses: department.emailAddresses || []
        }))
    }))
});

const mapUpdateRequest = (
    settings: TranscriptRoutingSettingsUI,
    botPublicId: string
): UpdateTranscriptRoutingRequest => ({
    botPublicId,
    enabled: settings.enabled,
    departments: settings.departments.map(department => ({
        id: department.id,
        name: department.name,
        summaryGuidance: department.summaryGuidance,
        filtrationEnabled: department.filtrationEnabled,
        eventTypes: department.eventTypes as unknown as Set<EventType>
    })),
    locations: settings.locations.map(location => ({
        locationPublicId: location.locationPublicId,
        locationName: location.locationName,
        catchAllRecipients: location.catchAllRecipients,
        departments: location.departments.map(department => ({
            departmentId: department.departmentId,
            departmentName: department.departmentName,
            emailAddresses: department.emailAddresses
        }))
    }))
});

export const fetchTranscriptRoutingSettings = createAsyncThunk(
    "transcriptRouting/fetch",
    async (botPublicId: string) => botApi.getTranscriptRoutingSettings({botPublicId})
);

export const updateTranscriptRoutingSettings = createAsyncThunk<TranscriptRoutingSettingsResponse, string, {state: RootState}>(
    "transcriptRouting/update",
    (botPublicId, {getState}) => botApi.updateTranscriptRoutingSettings({
        updateTranscriptRoutingRequest: mapUpdateRequest(getState().transcriptRouting.updateRequest, botPublicId)
    })
);

const initialState: TranscriptRoutingState = {
    updateRequest: emptySettings,
    requestStatus: RequestStatus.NONE,
    updateStatus: RequestStatus.NONE,
    hasUnsavedChanges: false
};

export const transcriptRoutingSlice = createSlice({
    name: "transcriptRouting",
    initialState,
    reducers: {
        setTranscriptRoutingEnabled: (state, {payload}: PayloadAction<boolean>) => {
            state.updateRequest.enabled = payload;
            state.hasUnsavedChanges = true;
        },
        addRoutingDepartment: (state) => {
            state.updateRequest.departments.push({
                name: "",
                summaryGuidance: "",
                filtrationEnabled: false,
                eventTypes: []
            });
            state.hasUnsavedChanges = true;
        },
        updateRoutingDepartment: (state, {payload}: PayloadAction<{
            index: number;
            patch: Partial<RoutingDepartment>;
        }>) => {
            const department = state.updateRequest.departments[payload.index];
            if (!department) {
                return;
            }

            const previousDepartment = {id: department.id, name: department.name};
            Object.assign(department, payload.patch);

            if (payload.patch.name !== undefined) {
                state.updateRequest.locations.forEach(location => {
                    location.departments.forEach(mapping => {
                        if (matchesDepartment(mapping, previousDepartment)) {
                            mapping.departmentName = payload.patch.name;
                        }
                    });
                });
            }

            state.hasUnsavedChanges = true;
        },
        removeRoutingDepartment: (state, {payload: index}: PayloadAction<number>) => {
            const department = state.updateRequest.departments[index];
            if (!department) {
                return;
            }

            state.updateRequest.departments.splice(index, 1);
            state.updateRequest.locations.forEach(location => {
                location.departments = location.departments.filter(mapping => !matchesDepartment(mapping, department));
            });
            state.hasUnsavedChanges = true;
        },
        updateRoutingLocation: (state, {payload}: PayloadAction<{
            index: number;
            patch: Partial<RoutingLocation>;
        }>) => {
            const location = state.updateRequest.locations[payload.index];
            if (!location) {
                return;
            }

            Object.assign(location, payload.patch);
            state.hasUnsavedChanges = true;
        },
        addRoutingLocationDepartment: (state, {payload: locationIndex}: PayloadAction<number>) => {
            const location = state.updateRequest.locations[locationIndex];
            const department = state.updateRequest.departments[0];
            if (!location || !department) {
                return;
            }

            location.departments.push({
                departmentId: department.id,
                departmentName: department.name,
                emailAddresses: []
            });
            state.hasUnsavedChanges = true;
        },
        updateRoutingLocationDepartment: (state, {payload}: PayloadAction<{
            locationIndex: number;
            departmentIndex: number;
            patch: Partial<RoutingLocationDepartment>;
        }>) => {
            const department = state.updateRequest.locations[payload.locationIndex]?.departments[payload.departmentIndex];
            if (!department) {
                return;
            }

            Object.assign(department, payload.patch);
            state.hasUnsavedChanges = true;
        },
        removeRoutingLocationDepartment: (state, {payload}: PayloadAction<{
            locationIndex: number;
            departmentIndex: number;
        }>) => {
            const location = state.updateRequest.locations[payload.locationIndex];
            if (!location?.departments[payload.departmentIndex]) {
                return;
            }

            location.departments.splice(payload.departmentIndex, 1);
            state.hasUnsavedChanges = true;
        },
        resetTranscriptRoutingSettings: (state) => {
            if (state.saved) {
                state.updateRequest = state.saved;
                state.hasUnsavedChanges = false;
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTranscriptRoutingSettings.pending, (state) => {
            state.requestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchTranscriptRoutingSettings.fulfilled, (state, {payload}) => {
            const settings = mapResponse(payload);
            state.saved = settings;
            state.updateRequest = settings;
            state.requestStatus = RequestStatus.RESOLVED;
            state.hasUnsavedChanges = false;
        });
        builder.addCase(fetchTranscriptRoutingSettings.rejected, (state) => {
            state.requestStatus = RequestStatus.REJECTED;
        });
        builder.addCase(updateTranscriptRoutingSettings.pending, (state) => {
            state.updateStatus = RequestStatus.PENDING;
        });
        builder.addCase(updateTranscriptRoutingSettings.fulfilled, (state, {payload}) => {
            const settings = mapResponse(payload);
            state.saved = settings;
            state.updateRequest = settings;
            state.updateStatus = RequestStatus.RESOLVED;
            state.hasUnsavedChanges = false;
        });
        builder.addCase(updateTranscriptRoutingSettings.rejected, (state) => {
            state.updateStatus = RequestStatus.REJECTED;
        });
    }
});

export const {
    setTranscriptRoutingEnabled,
    addRoutingDepartment,
    updateRoutingDepartment,
    removeRoutingDepartment,
    updateRoutingLocation,
    addRoutingLocationDepartment,
    updateRoutingLocationDepartment,
    removeRoutingLocationDepartment,
    resetTranscriptRoutingSettings
} = transcriptRoutingSlice.actions;
export const transcriptRoutingDepartmentsSelector = (state: RootState) => state.transcriptRouting.updateRequest.departments;
export const transcriptRoutingLocationsSelector = (state: RootState) => state.transcriptRouting.updateRequest.locations;
export const transcriptRoutingEnabledSelector = (state: RootState) => state.transcriptRouting.updateRequest.enabled;
export const transcriptRoutingRequestStatusSelector = (state: RootState) => state.transcriptRouting.requestStatus;
export const transcriptRoutingUpdateStatusSelector = (state: RootState) => state.transcriptRouting.updateStatus;
export const hasUnsavedTranscriptRoutingChangesSelector = (state: RootState) => state.transcriptRouting.hasUnsavedChanges;
export const selectRoutingDepartmentName = (index: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.departments[index]?.name;
export const selectRoutingDepartmentGuidance = (index: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.departments[index]?.summaryGuidance;
export const selectRoutingDepartmentFiltrationEnabled = (index: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.departments[index]?.filtrationEnabled;
export const selectRoutingDepartmentEvents = (index: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.departments[index]?.eventTypes;
export const selectRoutingCatchAllRecipients = (locationIndex: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.locations[locationIndex]?.catchAllRecipients || [];
export const selectRoutingLocationDepartmentName = (locationIndex: number, departmentIndex: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.locations[locationIndex]?.departments[departmentIndex]?.departmentName;
export const selectRoutingLocationDepartmentRecipients = (locationIndex: number, departmentIndex: number) => (state: RootState) =>
    state.transcriptRouting.updateRequest.locations[locationIndex]?.departments[departmentIndex]?.emailAddresses || [];

export default transcriptRoutingSlice.reducer;
